import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './style.scss';

const VB_COLORS = {
  black:     { bg: '#141414', fg: 'rgba(255,255,255,0.12)' },
  white:     { bg: '#f0ebe0', fg: '#111' },
  red:       { bg: '#bf2418', fg: '#fff' },
  crimson:   { bg: '#be123c', fg: '#fff' },
  rose:      { bg: '#fb7185', fg: '#111' },
  pink:      { bg: '#e91e8c', fg: '#fff' },
  magenta:   { bg: '#c026d3', fg: '#fff' },
  lavender:  { bg: '#a78bfa', fg: '#111' },
  violet:    { bg: '#7c3aab', fg: '#fff' },
  purple:    { bg: '#9333ea', fg: '#fff' },
  indigo:    { bg: '#4338ca', fg: '#fff' },
  blue:      { bg: '#1a6faf', fg: '#fff' },
  skyblue:   { bg: '#38bdf8', fg: '#111' },
  cyan:      { bg: '#06b6d4', fg: '#111' },
  turquoise: { bg: '#2dd4bf', fg: '#111' },
  teal:      { bg: '#0d9488', fg: '#fff' },
  mint:      { bg: '#34d399', fg: '#111' },
  green:     { bg: '#1e9e50', fg: '#fff' },
  lime:      { bg: '#84cc16', fg: '#111' },
  yellow:    { bg: '#e8b800', fg: '#111' },
  gold:      { bg: '#eab308', fg: '#111' },
  amber:     { bg: '#f59e0b', fg: '#111' },
  orange:    { bg: '#e06c10', fg: '#fff' },
};

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

// VS Code theme palettes. Colors assigned in order to consecutive non-black
// tile groups, wrapping when groups exceed palette length.
const VSCODE_THEMES = [
  { name: 'One Dark Pro',     colors: ['skyblue', 'violet',  'green',  'rose',    'amber',   'yellow'  ] },
  { name: 'Dracula',          colors: ['purple',  'cyan',    'pink',   'lime',    'yellow',  'crimson' ] },
  { name: 'Monokai Pro',      colors: ['orange',  'crimson', 'lime',   'turquoise','yellow', 'lavender'] },
  { name: 'Tokyo Night',      colors: ['indigo',  'purple',  'cyan',   'teal',    'blue',    'pink'    ] },
  { name: 'Solarized Dark',   colors: ['blue',    'teal',    'lime',   'gold',    'orange',  'red'     ] },
  { name: 'Nord',             colors: ['indigo',  'skyblue', 'mint',   'gold',    'rose',    'lavender'] },
  { name: 'Catppuccin Mocha', colors: ['pink',    'purple',  'crimson','yellow',  'green',   'blue'    ] },
  { name: 'GitHub Dark',      colors: ['blue',    'green',   'crimson','lavender','amber',   'pink'    ] },
];

// Shared across all FlapBoard instances so every loop board uses the same theme.
let _moduleThemeIndex = Math.floor(Math.random() * VSCODE_THEMES.length);
const _initialTheme = VSCODE_THEMES[_moduleThemeIndex % VSCODE_THEMES.length];
_moduleThemeIndex++; // first shuffle picks the theme after the initial one
let _shuffleTheme = null; // theme selected for the current flapboard-randomize event cycle

class FlapBoard extends Component {
  constructor(props) {
    super(props);
    this._unmounted = false;
    this.boardRef = null;
    this.tileRefs = [];
    this.observer = null;
    this._loopTimeout = null;
    this._suppressUpdate = false;
  }

  applyColors = (theme) => {
    let groupIndex = 0;
    this.paddedRows = this.paddedRows.map(row => {
      const newColors = [...row.colors];
      let i = 0;
      while (i < newColors.length) {
        if (newColors[i] !== 'black') {
          const color = theme.colors[groupIndex % theme.colors.length];
          groupIndex++;
          while (i < newColors.length && newColors[i] !== 'black') {
            newColors[i] = color;
            i++;
          }
        } else {
          i++;
        }
      }
      return { ...row, colors: newColors };
    });
  }

  handleRandomize = () => {
    if (!this.paddedRows) return;
    if (!_shuffleTheme) {
      // First instance to handle this event cycle picks the theme and broadcasts it.
      _shuffleTheme = VSCODE_THEMES[_moduleThemeIndex % VSCODE_THEMES.length];
      _moduleThemeIndex++;
      document.dispatchEvent(new CustomEvent('flapboard-theme-applied', {
        detail: { name: _shuffleTheme.name, colors: _shuffleTheme.colors },
      }));
      setTimeout(() => { _shuffleTheme = null; }, 0);
    }
    this.applyColors(_shuffleTheme);
    this._suppressUpdate = true;
    if (this._loopTimeout) {
      clearTimeout(this._loopTimeout);
      this._loopTimeout = null;
    }
    requestAnimationFrame(() => {
      if (!this._unmounted) {
        this._suppressUpdate = false;
        this.play(false);
      }
    });
  }

  componentDidMount() {
    if (this.props.autoPlay) {
      if (this.props.loop) {
        document.addEventListener('flapboard-randomize', this.handleRandomize);
        this.applyColors(_initialTheme);
        setTimeout(() => {
          if (!this._unmounted) {
            document.dispatchEvent(new CustomEvent('flapboard-theme-applied', {
              detail: { name: _initialTheme.name, colors: _initialTheme.colors },
            }));
          }
        }, 0);
      }
      this.play();
    } else {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.play();
            this.observer.disconnect();
            this.observer = null;
          }
        },
        { threshold: 0.3 }
      );
      if (this.boardRef) this.observer.observe(this.boardRef);
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    document.removeEventListener('flapboard-randomize', this.handleRandomize);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this._loopTimeout) {
      clearTimeout(this._loopTimeout);
      this._loopTimeout = null;
    }
  }

  setTile(ri, ci, ch, colorName, animate) {
    if (this._unmounted) return;
    const face = this.tileRefs[ri] && this.tileRefs[ri][ci];
    if (!face) return;
    const color = VB_COLORS[colorName] || VB_COLORS.black;
    face.style.backgroundColor = color.bg;
    face.style.color = color.fg;
    const iconClass = this.paddedRows[ri] && this.paddedRows[ri].icons[ci];
    if (iconClass) {
      face.innerHTML = `<i class="${iconClass}"></i>`;
    } else {
      face.textContent = ch === ' ' ? '' : ch;
    }
    if (animate) {
      const tile = face.parentNode;
      if (tile._flipTimeout) clearTimeout(tile._flipTimeout);
      tile.classList.remove('flipping');
      requestAnimationFrame(() => {
        if (!this._unmounted) {
          tile.classList.add('flipping');
          tile._flipTimeout = setTimeout(() => {
            tile.classList.remove('flipping');
            tile._flipTimeout = null;
          }, 300);
        }
      });
    }
  }

  play(blackOnly = false) {
    const rows = this.paddedRows;
    let maxSettle = 0;
    rows.forEach((row, ri) => {
      for (let ci = 0; ci < row.text.length; ci++) {
        const finalColor = row.colors[ci] || 'black';
        const isIcon = !!(row.icons && row.icons[ci]);
        if (blackOnly && finalColor !== 'black') continue;
        const startDelay = ci * 55 + ri * 180;
        if (!isIcon) {
          const cycles = 4 + Math.floor(Math.random() * 5);
          for (let k = 0; k < cycles; k++) {
            setTimeout(() => {
              const randCh = CHARS[Math.floor(Math.random() * CHARS.length)];
              this.setTile(ri, ci, randCh, 'black', true);
            }, startDelay + k * 55);
          }
          const settleAt = startDelay + cycles * 55;
          if (settleAt > maxSettle) maxSettle = settleAt;
          setTimeout(() => {
            const r = this.paddedRows[ri];
            if (r) this.setTile(ri, ci, r.text[ci], r.colors[ci] || 'black', true);
          }, settleAt);
        } else {
          if (startDelay > maxSettle) maxSettle = startDelay;
          setTimeout(() => {
            const r = this.paddedRows[ri];
            if (r) this.setTile(ri, ci, r.text[ci], r.colors[ci] || 'black', true);
          }, startDelay);
        }
      }
    });

    if (this.props.loop) {
      this._loopTimeout = setTimeout(() => {
        if (!this._unmounted) this.play(true);
      }, maxSettle + 3000);
    }
  }

  componentDidUpdate(prevProps) {
    if (this._suppressUpdate) return;
    if (prevProps.rows !== this.props.rows && this._prevPaddedRows) {
      this.paddedRows.forEach((row, ri) => {
        const prevRow = this._prevPaddedRows[ri];
        for (let ci = 0; ci < row.text.length; ci++) {
          const prevCh    = prevRow ? prevRow.text[ci]           : '';
          const prevColor = prevRow ? prevRow.colors[ci]         : 'black';
          const prevIcon  = prevRow ? (prevRow.icons[ci] || '')  : '';
          const curIcon   = row.icons[ci] || '';
          if (
            row.text[ci] !== prevCh ||
            (row.colors[ci] || 'black') !== prevColor ||
            curIcon !== prevIcon
          ) {
            this.setTile(ri, ci, row.text[ci], row.colors[ci] || 'black', true);
          }
        }
      });
    }
  }

  render() {
    const { rows, heading: Heading } = this.props;
    this._prevPaddedRows = this.paddedRows;
    const maxLen = Math.max(...rows.map(r => r.text.length));
    this.paddedRows = rows.map(row => {
      const pad = maxLen - row.text.length;
      const icons = row.icons || [];
      return {
        text: row.text + ' '.repeat(pad),
        colors: row.colors.concat(Array(pad).fill('black')),
        icons: icons.concat(Array(Math.max(0, maxLen - icons.length)).fill('')),
      };
    });

    this.paddedRows.forEach((_, ri) => {
      if (!this.tileRefs[ri]) this.tileRefs[ri] = [];
    });

    return (
      <div className="flap-board" ref={(el) => { this.boardRef = el; }}>
        {Heading && (
          <Heading className="visually-hidden">
            {rows.map(r => r.text).join(' ')}
          </Heading>
        )}
        {this.paddedRows.map((row, ri) => (
          <div key={ri} className="flap-row" aria-hidden={Heading ? true : undefined}>
            {row.text.split('').map((ch, ci) => (
              <div key={ci} className="flap-tile">
                <div
                  className="flap-face"
                  ref={(el) => { if (el) this.tileRefs[ri][ci] = el; }}
                  style={{
                    backgroundColor: VB_COLORS.black.bg,
                    color: VB_COLORS.black.fg,
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
}

FlapBoard.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    colors: PropTypes.arrayOf(PropTypes.string).isRequired,
    icons: PropTypes.arrayOf(PropTypes.string),
  })).isRequired,
  autoPlay: PropTypes.bool,
  loop: PropTypes.bool,
  heading: PropTypes.oneOf(['h1', 'h2', 'h3']),
};

FlapBoard.defaultProps = {
  autoPlay: false,
  loop: false,
};

export default FlapBoard;



// WEBPACK FOOTER //
// ./src/components/FlapBoard/FlapBoard.js